<h1 mat-dialog-title>Metrics Guide</h1>
<div mat-dialog-content>
  <p>This guide explains the metrics shown in the Weekly Summary and how to read them.</p>

  <h4>How to read a table cell</h4>
  <p>Each cell combines multiple signals to give you a quick read of performance for that time slot.</p>
  <div style="display: flex; gap: 12px; align-items: center;">
    <div aria-label="Sample cell" style="position: relative; width: 160px; height: 90px; border-radius: 8px; padding: 8px; box-sizing: border-box; background: #c8e6c9;">
      <div style="display: flex; justify-content: space-between; align-items: center;">
        <div style="display: flex; gap: 6px; align-items: center;">
          <mat-icon style="color:#2e7d32">currency_pound</mat-icon>
          <mat-icon style="color:#1e88e5">sentiment_satisfied</mat-icon>
          <mat-icon style="color:#f57c00">people</mat-icon>
        </div>
        <div title="More Sales Potential" style="width: 24px; height: 24px; border-radius: 4px; display: grid; place-items: center; background:#ef5350; color: #fff;">
          <mat-icon style="font-size:18px; height:18px; width:18px;">currency_pound</mat-icon>
        </div>
      </div>
      <div style="position: absolute; left: 8px; right: 8px; bottom: 8px; display: flex; align-items: center; gap: 6px; font-weight: 600;">
        <span>£450</span>
        <mat-icon color="accent">trending_up</mat-icon>
      </div>
    </div>
    <div style="font-size: 12px; line-height: 1.5;">
      <div><strong>Background</strong>: QPI (queue efficiency) color for this slot.</div>
      <div><strong>Top‑left icons</strong>: Sales, Experience, Staffing indicators (color‑coded).</div>
      <div><strong>Top‑right badge</strong>: More Sales Potential (if present).</div>
      <div><strong>Bottom‑left</strong>: Active metric value + short‑term trend vs last 3 weeks.</div>
    </div>
  </div>

  <h4>Sales</h4>
  <p>How current sales compare to what’s expected for the time of day given context (passerby, weather, recent trends). It defines an expected range for the slot.</p>
  <ul>
    <li><strong>Overdelivering</strong>: Above the expected range — conversion was stronger than conditions suggest.</li>
    <li><strong>Balanced</strong>: Within the expected range.</li>
    <li><strong>Underperforming</strong>: Below the expected range — opportunity to better capitalize on demand.</li>
  </ul>
  <p style="font-size:12px;color:rgba(0,0,0,0.65)">Trend arrows compare today versus the slot’s 3‑week average.</p>

  <h4>QPI (Queue Performance Indicator)</h4>
  <p>Queue efficiency on a 0–100% scale. Higher is better. It compares this slot’s operations to historical days with similar busyness but higher sales, and summarizes differences in durations and bottlenecks.</p>
  <ul>
    <li><strong>Value</strong>: 0–100% efficiency for the selected slot/period.</li>
    <li><strong>Colors</strong>: Red → Orange → Yellow → Green as efficiency improves.</li>
    <li><strong>In cells</strong>: The background color reflects QPI.</li>
    <li><strong>In details</strong>: A gradient bar shows the exact efficiency with a marker.</li>
  </ul>

  <h4>Guest Experience</h4>
  <p>How current service speed compares to the slot’s recent average (past weeks) — what guests usually experience at this time and whether we delivered it.</p>
  <ul>
    <li><strong>Exceptional</strong>: Faster than the slot’s recent average (better than guests usually expect).</li>
    <li><strong>Ok</strong>: Around the slot’s recent average.</li>
    <li><strong>Bad</strong>: Slower than the slot’s recent average.</li>
  </ul>
  <p style="font-size:12px;color:rgba(0,0,0,0.65)">Trend arrows compare today versus the slot’s 3‑week average. Lower duration is better; a down arrow can indicate improvement.</p>

  <h4>Staffing</h4>
  <p>Inferred from Cost of Labor (CoL = labor cost ÷ sales).</p>
  <ul>
    <li><strong>Understaffed</strong>: CoL below target; may need more staff to capture demand.</li>
    <li><strong>Adequate</strong>: CoL within target range.</li>
    <li><strong>Overstaffed</strong>: CoL above target; costs too high for demand.</li>
    <li><i>“Unlikely” flags</i> highlight conflicting signals (e.g., CoL suggests overstaffed but QPI is poor).</li>
  </ul>

  <h4>More Sales Potential</h4>
  <p>Likelihood of increasing sales by improving queue efficiency, based on operational estimates.</p>
  <ul>
    <li><strong>Likely</strong> • <strong>Possible</strong> • <strong>Unlikely</strong></li>
  </ul>
  <p style="font-size:12px;color:rgba(0,0,0,0.65)">Appears in the cell’s top‑right when relevant. Often associated with lower QPI and sales at/below expectation.</p>

  <h4>Cell Trend Arrows</h4>
  <p>Short‑term trend vs the slot’s recent average (last 3 weeks).</p>
  <ul>
    <li><strong><mat-icon>trending_up</mat-icon> Trending Up</strong>: Higher than recent average.</li>
    <li><strong><mat-icon>trending_flat</mat-icon> Trending Flat</strong>: Around recent average.</li>
    <li><strong><mat-icon>trending_down</mat-icon> Trending Down</strong>: Lower than recent average.</li>
  </ul>
  <p style="font-size:12px;color:rgba(0,0,0,0.65)">Note: For Experience and CoL, lower can be good; interpret arrows together with the metric.</p>

  <h4>Tips</h4>
  <ul>
    <li><strong>Click any cell</strong> to open detailed tabs for Sales, Queue, Experience, and Staffing.</li>
    <li><strong>Use the filter tabs</strong> above the grid to focus on Sales, Guest Experience, or Staffing.</li>
  </ul>
</div>
<div mat-dialog-actions align="end">
  <button mat-button (click)="onClose()" cdkFocusInitial>Close</button>
</div>
